import { CurrencyInr, Paperclip, Plus, Trash } from "@phosphor-icons/react"
import { Button, FormInput } from "@vesatogo/grass-core"
import classNames from "classnames"
import React from "react"

interface DetailTableProps {
  headers: any[]
  rows: any[]
  onAddRow: () => void
  onRemoveRow: (index: number) => void
  onAttachRow?: (index: number) => void
  addRowLabel: string
  inputProps: any
  namePrefix: string
  className?: string
  addDisable?: boolean
  showTotals?: boolean
  totals?: any
  deleteDisable?: boolean
}

const DetailTable = ({
  headers,
  rows,
  onAddRow,
  onRemoveRow,
  onAttachRow,
  addRowLabel,
  inputProps,
  namePrefix,
  className,
  addDisable,
  deleteDisable,
  showTotals,
  totals,
}: DetailTableProps) => {
  return (
    <div className="mt-4">
      <div
        className={classNames(
          `grid gap-2 text-xs border-y-1 border-gray-300 py-2 uppercase bg-gray-100 items-center`,
          className
        )}
      >
        {headers.map((h: any, i: number) => {
          if (h.hideInHeader) return null
          return (
            <div
              key={i}
              className={classNames(`px-2 ${h.className || ""}`, {
                [`col-span-${h.headerColSpan}`]: h.headerColSpan,
                [`col-span-${h.colSpan}`]: h.colSpan && !h.headerColSpan,
              })}
            >
              <div
                className={classNames("flex items-center gap-6", {
                  "justify-between": i === headers.length - 1,
                })}
              >
                {i === 0 && <div className="text-center pl-2">#</div>}
                <div className="flex items-center gap-1">
                  {h.label}{" "}
                  {h.required && <span className="text-red-500">*</span>}
                </div>
                {i === headers.length - 1 && (
                  <div className="flex items-center gap-2">
                    {onAttachRow && <div className="w-8" />}
                    <Button
                      onClick={onAddRow}
                      leftIcon={<Plus />}
                      size="sm"
                      intent="info"
                      variant="minimal"
                      className="underline"
                      disabled={addDisable}
                    >
                      {addRowLabel}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
      <div className={classNames(`grid gap-2  items-center`, className)}>
        {rows.map((row: any, rowIndex: number) => (
          <React.Fragment key={rowIndex}>
            {headers?.map((h: any, colIndex: number) => {
              if (h.hideInRow) return null
              return (
                <div
                  key={colIndex}
                  className={classNames("py-2 pr-2", {
                    [`col-span-${h.colSpan}`]: h.colSpan,
                  })}
                >
                  {h.render ? (
                    <div className="flex items-center gap-2">
                      {h.render(rowIndex)}
                      {colIndex === headers.length - 1 && (
                        <div className="flex items-center gap-2">
                          {onAttachRow && (
                            <Button
                              variant="ghost"
                              leftIcon={<Paperclip size={14} />}
                              onClick={() => onAttachRow(rowIndex)}
                              intent="info"
                              size="sm"
                            >
                              {rows[rowIndex]?.attachments?.length || 0}
                            </Button>
                          )}
                          <Button
                            intent="danger"
                            variant="minimal"
                            disabled={deleteDisable}
                            size="sm"
                            leftIcon={
                              <Trash
                                size={14}
                                className={classNames(
                                  "text-red-400 cursor-pointer w-[20px] !m-0",
                                  {
                                    "!opacity-50 !cursor-not-allowed":
                                      deleteDisable,
                                  }
                                )}
                                onClick={() => {
                                  deleteDisable ? null : onRemoveRow(rowIndex)
                                }}
                              />
                            }
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-4">
                      {colIndex === 0 && (
                        <div className="w-6 h-6 rounded-full border bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-400 ml-2">
                          {rowIndex + 1}
                        </div>
                      )}
                      <FormInput
                        {...inputProps(`${namePrefix}.${rowIndex}.${h.field}`)}
                        placeholder={h.placeholder || "Enter"}
                        className={"!w-full"}
                        {...h.props}
                      />
                      {colIndex === headers.length - 1 && (
                        <div className="flex items-center gap-2">
                          {onAttachRow && (
                            <Button
                              variant="ghost"
                              leftIcon={<Paperclip size={14} />}
                              onClick={() => onAttachRow(rowIndex)}
                              intent="info"
                              size="sm"
                            >
                              {rows[rowIndex]?.attachments?.length || 0}
                            </Button>
                          )}
                          <Trash
                            size={14}
                            className={classNames(
                              "text-red-400 cursor-pointer w-[20px]",
                              {
                                "opacity-50 cursor-not-allowed": deleteDisable,
                              }
                            )}
                            onClick={() => {
                              deleteDisable ? null : onRemoveRow(rowIndex)
                            }}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </React.Fragment>
        ))}
      </div>
      {showTotals && (
        <div
          className={classNames(
            `grid gap-2 items-center border-t border-gray-300 border-dashed bg-gray-50/50`,
            className
          )}
        >
          {headers.map((h: any, colIndex: number) => {
            const isCost = h.label?.toLowerCase().includes("cost")
            const isQty = h.label?.toLowerCase().includes("qty")
            const total =
              totals?.[h.field] !== undefined
                ? totals[h.field]
                : rows.reduce((sum, row) => {
                    const field = h.field
                    return sum + Number(row[field] || 0)
                  }, 0)

            return (
              <React.Fragment key={colIndex}>
                {h.hideInTotal ? (
                  <div />
                ) : (
                  <div
                    className={classNames("py-2 pr-2", {
                      [`col-span-${h.totalColSpan}`]: h.totalColSpan,
                      [`col-span-${h.colSpan}`]: h.colSpan && !h.totalColSpan,
                    })}
                  >
                    <div className="flex items-center gap-2">
                      {colIndex === 0 && (
                        <div className="text-sm font-semibold text-gray-400 ml-2 w-12">
                          Total
                        </div>
                      )}
                      {h.showTotal ? (
                        <FormInput
                          value={total || "00"}
                          disabled
                          leftElement={isCost ? <CurrencyInr /> : undefined}
                          rightElement={
                            isQty ? (
                              <span className="text-brown-300 pr-4 text-sm font-400">
                                Unit
                              </span>
                            ) : undefined
                          }
                          className="!w-full"
                        />
                      ) : (
                        <div className="flex-1" />
                      )}
                    </div>
                  </div>
                )}
              </React.Fragment>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default DetailTable
